import {
  EtsyListingTypeEnum,
  extractEtsyListingId,
  EtsyParamIncludesEnum,
  mapEtsyListingToCreateDraftListingRequest,
} from '@coolcolduk/etsy';
import {useMessage} from '@coolcolduk/ui-mui';
import {validateErrorRequest} from '@coolcolduk/util';
import {Button, LinearProgress, Stack, TextField, Typography} from '@mui/material';
import {get} from 'lodash';
import {useState} from 'react';
import createDraftListingHelper from '../../../hook/request/createDraftListingHelper';
import fetchShopReadinessStateDefinitions from '../../../hook/request/fetchShopReadinessStateDefinitions';
import fetchShopShippingProfiles from '../../../hook/request/fetchShopShippingProfiles';
import {fetchListing} from '../../../hook/useGetListing';
import {firebaseAuth} from '../../../setup/firebase';
import {FeatureProps} from '../../../types/FeatureObject';
import {errorMap} from '../../../util/error/errorMap';
import messageWhenThrown from '../../../util/error/messageWhenThrown';

export interface EtsyCopyListingProps extends FeatureProps {}

export default function EtsyCopyListing({selectedAccount}: EtsyCopyListingProps) {
  const {showMessage} = useMessage();
  const [listingLink, setListingLink] = useState('');
  const [progress, setProgress] = useState(-1);

  const handleCopyListing = async () => {
    const listingId = extractEtsyListingId(listingLink);
    if (!listingId) {
      showMessage('Invalid Etsy listing URL', undefined, 'error');
      return;
    }

    setProgress(0);
    const result = await messageWhenThrown(async () => {
      const token = await firebaseAuth.currentUser?.getIdToken();
      if (!token) {
        showMessage('Token error, please try to refresh the page or login again');
        return undefined;
      }

      const sourceListing = await fetchListing(token, selectedAccount.id, listingId.toString(), [
        EtsyParamIncludesEnum.IMAGES,
      ]);
      validateErrorRequest(sourceListing);

      const [shopProfiles, readinessStates] = await Promise.all([
        fetchShopShippingProfiles(token, selectedAccount.id),
        fetchShopReadinessStateDefinitions(token, selectedAccount.id),
      ]);
      const firstProfileId = shopProfiles?.results?.find((p) => !p.is_deleted)?.shipping_profile_id ?? null;
      const firstReadinessStateId = readinessStates?.results?.[0]?.readiness_state_id ?? null;
      const isPhysical =
        sourceListing.listing_type === EtsyListingTypeEnum.PHYSICAL ||
        sourceListing.listing_type === EtsyListingTypeEnum.BOTH;
      if (isPhysical && firstProfileId == null) {
        showMessage(
          'Your shop has no shipping profile. Create one in Etsy before copying a physical listing.',
          undefined,
          'error',
        );
        setProgress(-1);
        return;
      }
      if (isPhysical && firstReadinessStateId == null) {
        showMessage(
          'Your shop has no processing profile (readiness state). Create one in Etsy before copying a physical listing.',
          undefined,
          'error',
        );
        setProgress(-1);
        return;
      }

      const draftListing = mapEtsyListingToCreateDraftListingRequest(sourceListing);
      const {
        shipping_profile_id: _droppedShippingProfile,
        shop_section_id: _droppedShopSection,
        ...draftWithoutShippingAndSection
      } = draftListing;
      const draftWithoutImageIds = {
        ...draftWithoutShippingAndSection,
        image_ids: [],
        shipping_profile_id: firstProfileId,
        readiness_state_id: firstReadinessStateId,
        shop_section_id: null,
      };
      await createDraftListingHelper(
        token,
        selectedAccount.id,
        draftWithoutImageIds,
        sourceListing.skus?.[0],
        sourceListing.listing_id,
        undefined,
        undefined,
      );
    });

    const success = result === undefined;
    const errorMessage = typeof result === 'string' ? get(errorMap, result, result) : 'Failed to create draft listing';
    showMessage(success ? 'Draft listing created' : errorMessage, undefined, success ? 'success' : 'error');
    if (success) {
      setListingLink('');
    }
    setProgress(-1);
  };

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Paste an Etsy listing URL from your selected account. A draft will be created in the same shop with details
        (title, description, tags, variations, personalisation, images) copied from that listing.
      </Typography>

      <TextField
        fullWidth
        label="Listing link"
        value={listingLink}
        onChange={(e) => setListingLink(e.target.value)}
        helperText="Etsy listing URL from the selected account (e.g. https://www.etsy.com/listing/...)"
        placeholder="https://www.etsy.com/listing/..."
      />

      <Button
        variant="contained"
        color="primary"
        disabled={!listingLink.trim()}
        loading={progress >= 0}
        onClick={handleCopyListing}
        loadingIndicator={<LinearProgress sx={{width: '100%'}} variant="indeterminate" color="inherit" />}
        sx={{'& > * > .MuiButton-loadingIndicator': {width: '80%'}}}
      >
        Copy listing
      </Button>
    </Stack>
  );
}
